import { IOptionV2, TypographyV2, HoverablePopoverV2 } from "components/v2"
import { useCurrentLanguage } from "store/hooks"
import { Language } from "@prisma/client"
import { classNames } from "utils"
import { useMemo } from "react"

interface ISingleSelectTriggerV2Props {
    optionKey: string | number
    getOption: (option: string | number) => IOptionV2
    placeholder?: string
    shouldTruncate?: boolean
}

const MAX_TITLE_LENGTH = 47

const SingleSelectTriggerV2 = ({
    optionKey,
    getOption,
    placeholder,
    shouldTruncate = false
}: ISingleSelectTriggerV2Props) => {
    const language = useCurrentLanguage()
    const isHeLanguage = language === Language.he
    const option = getOption(optionKey)
    const Icon = option?.icon

    const needsTruncation = useMemo(() => {
        return shouldTruncate && option?.title?.length > MAX_TITLE_LENGTH
    }, [shouldTruncate, option?.title])

    const displayTitle = useMemo(() => {
        if (!option?.title) return ""
        return needsTruncation ? `${option.title.slice(0, MAX_TITLE_LENGTH)}...` : option.title
    }, [option?.title, needsTruncation])

    const TooltipContent = () => {
        if (!option?.title) return null

        return (
            <div className="bg-white rounded-lg p-3 shadow-lg  border-gray-300 border pointer-events-auto max-w-xs">
                <div className="flex items-center text-gray-800 rounded-lg text-sm">
                    {option?.media &&
                        (typeof option.media === "string" ? (
                            <img src={option.media} alt={option.title} className="w-5 h-5 rounded-full mr-2" />
                        ) : (
                            <div className="mr-2">{option.media}</div>
                        ))}
                    <span className="whitespace-normal">{option.title}</span>
                </div>
            </div>
        )
    }

    const content = (
        <span className="flex items-center truncate flex-grow h-11 leading-5">
            {optionKey !== undefined ? (
                <div className="flex items-center gap-x-2.5">
                    {option &&
                        option.media &&
                        (typeof option.media === "string" ? (
                            <img src={option.media} alt={option.title} className="w-6 h-6 rounded-full" />
                        ) : (
                            option.media
                        ))}

                    <div className="flex items-center">
                        {Icon && <Icon className={classNames("text-gray-500", isHeLanguage ? "ml-3" : "mr-3")} />}
                        <TypographyV2 type="sm" width="medium">
                            {displayTitle}
                        </TypographyV2>
                    </div>
                </div>
            ) : (
                <span className="text-[#9CA3AF] font-normal text-xs leading-5">{placeholder}</span>
            )}
        </span>
    )

    // If tooltip is not needed, return content directly
    if (!needsTruncation || !option?.title) {
        return content
    }

    // Wrap with tooltip if needed
    return (
        <HoverablePopoverV2 classNames="!w-full" placement="top" content={<TooltipContent />}>
            {content}
        </HoverablePopoverV2>
    )
}

export default SingleSelectTriggerV2
